import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Bell, Loader2, Plus, Send, X } from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const NotificationsCard = () => {
    const [emails, setEmails] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/settings/notifications");
            setEmails(data?.emails || []);
        } catch (err) {
            if (process.env.NODE_ENV !== "production") {
                console.warn("Failed to load notification emails:", err?.message || err);
            }
            toast.error("Erro a carregar emails de alerta.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const persist = async (next) => {
        setSaving(true);
        try {
            const { data } = await api.put(
                "/admin/settings/notifications", { emails: next },
            );
            setEmails(data?.emails || []);
        } catch (err) {
            const detail = err?.response?.data?.detail || err?.message;
            toast.error(`Erro a guardar: ${detail}`);
        } finally {
            setSaving(false);
        }
    };

    const addEmail = async () => {
        const v = input.trim().toLowerCase();
        if (!v) return;
        if (!EMAIL_RE.test(v)) {
            toast.error("Email inválido.");
            return;
        }
        if (emails.includes(v)) {
            toast.info("Esse email já está na lista.");
            setInput("");
            return;
        }
        const next = [...emails, v];
        setInput("");
        await persist(next);
        toast.success("Email adicionado.");
    };

    const removeEmail = async (email) => {
        const next = emails.filter((e) => e !== email);
        await persist(next);
        toast.success("Email removido.");
    };

    const sendTest = async () => {
        if (emails.length === 0) {
            toast.error("Adiciona pelo menos um email primeiro.");
            return;
        }
        setTesting(true);
        try {
            const { data } = await api.post("/admin/settings/notifications/test");
            toast.success(`Email de teste enviado para ${data.sent_to?.length || 0} destinatário(s).`);
        } catch (err) {
            const detail = err?.response?.data?.detail || err?.message;
            toast.error(`Falha no teste: ${detail}`);
        } finally {
            setTesting(false);
        }
    };

    return (
        <div
            className="bg-white rounded-2xl ring-1 ring-stone-200 p-6 md:p-8"
            data-testid="notifications-card"
        >
            <div className="flex items-start gap-3 mb-5">
                <div className="bg-amber-100 rounded-xl p-2.5 shrink-0">
                    <Bell className="h-5 w-5 text-amber-700" />
                </div>
                <div className="flex-1 min-w-0">
                    <h2 className="font-serif text-2xl text-stone-900">Alertas de novos leads</h2>
                    <p className="text-sm text-stone-500 mt-0.5">
                        Sempre que chega um novo lead, enviamos um email a cada um destes endereços com os detalhes.
                    </p>
                </div>
            </div>

            {/* Add new */}
            <div className="flex flex-col sm:flex-row gap-2 mb-5">
                <div className="flex-1">
                    <Label htmlFor="notif-email" className="sr-only">Adicionar email</Label>
                    <Input
                        id="notif-email"
                        type="email"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
                        placeholder="ex: synarapersonalchef@gmail.com"
                        className="h-11"
                        data-testid="notif-email-input"
                    />
                </div>
                <Button
                    onClick={addEmail}
                    disabled={saving}
                    className="rounded-full bg-stone-900 hover:bg-stone-800 text-stone-50 h-11"
                    data-testid="notif-email-add"
                >
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                    Adicionar
                </Button>
            </div>

            {/* List */}
            {loading ? (
                <div className="text-sm text-stone-400 py-4">A carregar…</div>
            ) : emails.length === 0 ? (
                <div className="bg-stone-50 ring-1 ring-stone-200 rounded-xl p-5 text-center text-sm text-stone-500">
                    Sem emails configurados. Adiciona pelo menos um para receber alertas.
                </div>
            ) : (
                <ul className="space-y-2" data-testid="notif-email-list">
                    {emails.map((email) => (
                        <li
                            key={email}
                            className="flex items-center justify-between gap-3 px-4 py-3 bg-stone-50 ring-1 ring-stone-200 rounded-xl"
                            data-testid={`notif-email-${email}`}
                        >
                            <div className="flex items-center gap-3 min-w-0">
                                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                                <span className="text-stone-800 truncate">{email}</span>
                            </div>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => removeEmail(email)}
                                disabled={saving}
                                className="h-8 w-8 hover:bg-rose-50 hover:text-rose-600"
                                aria-label={`Remover ${email}`}
                                data-testid={`notif-email-remove-${email}`}
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </li>
                    ))}
                </ul>
            )}

            {/* Test */}
            <div className="mt-6 pt-5 border-t border-stone-100 flex items-center justify-between gap-3">
                <p className="text-xs text-stone-500">
                    Envia um lead de exemplo para confirmares que os emails chegam.
                </p>
                <Button
                    variant="outline"
                    onClick={sendTest}
                    disabled={testing || emails.length === 0}
                    data-testid="notif-email-test"
                >
                    {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                    Enviar teste
                </Button>
            </div>
        </div>
    );
};

export default NotificationsCard;
